import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useDebouncedCallback, useFilterChangeCallback, useRuntimeSetting } from 'shared/hooks';
import { selectDealType } from 'shared/selectors/jsonQuery';

import { FILTER_KEY } from '../constants';
import { useDailyRentPriceCorrection } from './useDailyRentPriceCorrection';
import { FDealType } from '../../../../packages/JsonQuery';

export const usePriceChangeHandler = () => {
  const onChange = useFilterChangeCallback(FILTER_KEY);
  const dealType = useSelector(selectDealType);
  const isDailyRent = dealType === FDealType.RentDaily;

  const debounceDelay = useRuntimeSetting<number>('desktopFiltersMicrofrontend.inputDebounceDelay', 1000);

  const { showCorrectionHint, wrapMinChange, wrapMaxChange } = useDailyRentPriceCorrection();

  const onPriceMinChange = useDebouncedCallback(
    (gte: number | null) => {
      if (isDailyRent) {
        wrapMinChange(gte, onChange);
      } else {
        onChange({ action: 'setPriceMin', arguments: [gte] });
      }
    },
    debounceDelay,
    [isDailyRent, wrapMinChange, onChange],
  );

  const onPriceMaxChange = useDebouncedCallback(
    (lte: number | null) => {
      if (isDailyRent) {
        wrapMaxChange(lte, onChange);
      } else {
        onChange({ action: 'setPriceMax', arguments: [lte] });
      }
    },
    debounceDelay,
    [isDailyRent, wrapMaxChange, onChange],
  );

  return useMemo(
    () => ({
      onChangeMin: onPriceMinChange,
      onChangeMax: onPriceMaxChange,
      showCorrectionHint: isDailyRent && showCorrectionHint,
    }),
    [onPriceMaxChange, onPriceMinChange, isDailyRent, showCorrectionHint],
  );
};
